import { Link } from "react-router-dom";
import { useState } from "react";

export function Footer() {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Build info from environment variables (injected at build time)
  const buildBranch = import.meta.env.VITE_BUILD_BRANCH || "dev";
  const buildDate = import.meta.env.VITE_BUILD_DATE || "";
  const buildCommit = import.meta.env.VITE_BUILD_COMMIT || "";
  
  // Format the build date if available
  const formattedDate = buildDate 
    ? new Date(buildDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "";

  return (
    <footer className="bg-surface-900 border-t border-surface-800/50">
      <div className="px-4 sm:px-6 py-2">
        {/* Collapsed view - just the footer toggle button */}
        {!isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs text-surface-500 hover:text-surface-300 transition-colors group"
          >
            <svg
              className="w-3 h-3 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            <span>Show Terms and Privacy</span>
          </button>
        )}

        {/* Expanded view - full footer content */}
        {isExpanded && (
          <>
            {/* Collapse button */}
            <div className="flex justify-center py-1">
              <button
                onClick={() => setIsExpanded(false)}
                className="flex items-center justify-center gap-2 py-1 text-xs text-surface-500 hover:text-surface-300 transition-colors"
              >
                <svg
                  className="w-3 h-3 transition-transform rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                <span>Hide Terms and Privacy</span>
              </button>
            </div>

        {/* Top row - Logo, links, email */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6 pt-2">
          {/* Left side - Colvix logo */}
          <a
            href="https://www.colvix.net"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-80 hover:opacity-100 transition-opacity"
          >
            <img
              src="/colvix_footer_final.png"
              alt="Colvix"
              className="colvix-logo h-5 sm:h-6"
            />
          </a>

          {/* Right side - Links */}
          <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-1 text-xs sm:text-sm">
            <Link
              to="/terms"
              className="text-surface-300 hover:text-primary-400 transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="text-surface-300 hover:text-primary-400 transition-colors"
            >
              Privacy
            </Link>
            <a
              href="mailto:support@colvix.net"
              className="text-surface-300 hover:text-primary-400 transition-colors"
            >
              support@colvix.net
            </a>
          </div>
        </div>

        {/* Legal disclaimer - always shown when expanded */}
        <div className="space-y-2 text-xs text-surface-400 leading-relaxed mb-4">
          <p>
            Colvix LLC provides TradeCraft as a platform for algorithmic strategy development and rule-based trading research. 
            We are not a broker, investment advisor, or fiduciary. Our platform is designed to help you make informed, independent 
            investment decisions. All data, analyses, backtests, metrics, signals, and platform features are provided "AS-IS" without 
            any warranties or guarantees of future performance.
          </p>
          <p>
            Trademarks and logos are the property of their respective owners and do not represent endorsements of any kind.
          </p>
        </div>

        {/* Bottom row - Build info */}
        <div className="pt-3 sm:pt-4 border-t border-surface-800/50 flex flex-wrap items-center justify-between gap-2 text-[10px] sm:text-xs text-surface-500 pb-2">
          <span>© {new Date().getFullYear()} TradeCraft</span>
          {(buildBranch || formattedDate) && (
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
              <span className="text-primary-400">{buildBranch}</span>
              {formattedDate && (
                <>
                  <span className="text-surface-600 hidden sm:inline">•</span>
                  <span className="hidden sm:inline">{formattedDate}</span>
                </>
              )}
              {buildCommit && (
                <span className="text-surface-600 font-mono hidden sm:inline" title={buildCommit}>
                  ({buildCommit.slice(0, 7)})
                </span>
              )}
            </span>
          )}
        </div>
          </>
        )}
      </div>
    </footer>
  );
}
